import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth';
import {
  LayoutDashboard,
  Users,
  Video,
  ClipboardList,
  HelpCircle,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  BarChart3,
  FileText,
  FileStack,
  Download,
  Brain,
  SlidersHorizontal,
} from 'lucide-react';
import { UserRole } from '@/types';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SkipLinks } from '@/components/accessibility/SkipLinks';
import { useGlobalShortcuts } from '@/components/KeyboardShortcuts';
import { MobileNavigation } from '@/components/MobileNavigation';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useUIStore } from '@/stores/uiStore';
import { useThemeStore } from '@/stores/themeStore';

interface NavigationItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: UserRole[];
}

const navigationItems: NavigationItem[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    roles: ['admin', 'manager', 'reviewer', 'associate'],
  },
  {
    name: 'Analytics',
    path: '/analytics',
    icon: BarChart3,
    roles: ['admin', 'manager'],
  },
  {
    name: 'Calibration',
    path: '/analytics/calibration',
    icon: SlidersHorizontal,
    roles: ['admin', 'manager'],
  },
  {
    name: 'Associates',
    path: '/associates',
    icon: Users,
    roles: ['admin', 'manager'],
  },
  {
    name: 'Interviews',
    path: '/interviews',
    icon: Video,
    roles: ['admin', 'manager', 'reviewer'],
  },
  {
    name: 'Review Queue',
    path: '/review-queue',
    icon: ClipboardList,
    roles: ['admin', 'manager', 'reviewer'],
  },
  {
    name: 'Questions',
    path: '/questions',
    icon: HelpCircle,
    roles: ['admin', 'manager'],
  },
  {
    name: 'Templates',
    path: '/templates',
    icon: FileStack,
    roles: ['admin', 'manager'],
  },
  {
    name: 'IQ Questions',
    path: '/iq-questions',
    icon: Brain,
    roles: ['admin', 'manager'],
  },
  {
    name: 'Reports',
    path: '/reports',
    icon: Download,
    roles: ['admin', 'manager'],
  },
  {
    name: 'Audit Logs',
    path: '/audit-logs',
    icon: FileText,
    roles: ['admin'],
  },
  {
    name: 'Settings',
    path: '/settings',
    icon: Settings,
    roles: ['admin', 'manager'],
  },
];

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const { confirmDialogOpen, confirmDialogConfig, closeConfirmDialog } = useUIStore();
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  // Enable global keyboard shortcuts
  useGlobalShortcuts(navigate);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
      navigate('/login');
    }
  };

  const filteredNavigation = navigationItems.filter((item) =>
    user?.role ? item.roles.includes(user.role) : false
  );

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      <SkipLinks />
      <div className={`flex min-h-screen overflow-hidden ${isDark ? 'mm-workplace mm-workplace-bg text-white' : 'mm-light mm-light-bg text-gray-900'}`}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        id="main-navigation"
        className={`
          fixed inset-y-0 left-0 z-50 w-72 sm:w-80 md:w-64 transform transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 safe-area-left
          ${isDark ? 'mm-glass border border-white/10' : 'bg-white border-r border-gray-200 shadow-lg'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex h-full flex-col">
          {/* Logo and close button */}
          <div className={`flex h-16 items-center justify-between px-4 sm:px-6 safe-area-top ${isDark ? 'border-b border-white/10' : 'border-b border-gray-200'}`}>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-bold mm-text-gradient truncate">MM Solutions</h1>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>English Assessment</p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className={`lg:hidden focus:outline-none focus:ring-2 focus:ring-violet-400 rounded p-2 ml-2 touch-target ${isDark ? 'text-white/70 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              aria-label="Close navigation"
            >
              <X size={24} aria-hidden="true" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4 scrollbar-custom safe-area-bottom" role="list">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActivePath(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    group flex items-center rounded-xl px-4 py-3 text-sm sm:text-base font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-400 touch-target
                    ${isDark
                      ? active
                        ? 'bg-white/10 text-white border border-white/10'
                        : 'text-white/70 hover:bg-white/5 hover:text-white'
                      : active
                        ? 'bg-violet-50 text-violet-700 border border-violet-200'
                        : 'bg-gray-200 text-gray-900 border border-gray-300 hover:bg-gray-300 hover:text-gray-900'
                    }
                  `}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon
                    className={`mr-3 h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 ${
                      isDark
                        ? active ? 'text-violet-200' : 'text-white/40 group-hover:text-white/70'
                        : active ? 'text-violet-600' : 'text-gray-700 group-hover:text-gray-900'
                    }`}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">{item.name}</span>
                  {active && <ChevronRight className={`ml-auto h-4 w-4 flex-shrink-0 ${isDark ? '' : 'text-violet-600'}`} aria-hidden="true" />}
                </Link>
              );
            })}
          </nav>

          {/* User info and logout */}
          <div className={`p-4 safe-area-bottom ${isDark ? 'border-t border-white/10' : 'border-t border-gray-200'}`}>
            <div className={`mb-3 rounded-2xl p-3 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
              <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.full_name}</p>
              <p className={`mt-0.5 text-xs truncate ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{user?.email}</p>
              <p className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${isDark ? 'bg-violet-500/15 text-violet-200' : 'bg-violet-200 text-violet-900'}`}>
                {user?.role}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className={`flex w-full items-center rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-violet-400 touch-target ${isDark ? 'text-white/80 hover:bg-white/5' : 'text-gray-800 hover:bg-gray-200'}`}
              aria-label="Sign out of application"
            >
              <LogOut className={`mr-3 h-5 w-5 flex-shrink-0 ${isDark ? 'text-white/50' : 'text-gray-700'}`} aria-hidden="true" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top header */}
        <header className={`flex h-16 items-center justify-between px-4 lg:px-6 safe-area-top safe-area-right ${isDark ? 'border-b border-white/10 mm-glass' : 'border-b border-gray-200 bg-white shadow-sm'}`} role="banner">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`lg:hidden focus:outline-none focus:ring-2 focus:ring-violet-400 rounded p-2 touch-target ${isDark ? 'text-white/70 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
            >
              <Menu size={24} aria-hidden="true" />
            </button>

            <div className="flex items-center lg:hidden min-w-0">
              <span className="text-base sm:text-lg font-semibold mm-text-gradient truncate">MM Solutions</span>
            </div>

            <div className="hidden lg:block">
              {/* Breadcrumb or page title could go here */}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <ThemeToggle />
            <div className="hidden md:block text-right min-w-0">
              <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.full_name}</p>
              <p className={`text-xs capitalize ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{user?.role}</p>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="flex-1 overflow-y-auto scrollbar-custom pb-safe" role="main">
          <div className="mx-auto w-full max-w-[1800px] px-4 sm:px-5 lg:px-6 py-4 sm:py-5 lg:py-6 pb-4 sm:pb-6 lg:pb-8">
            <Breadcrumbs />
            {children}
          </div>
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <MobileNavigation />

      {/* Global Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        onClose={closeConfirmDialog}
        config={confirmDialogConfig}
      />
    </div>
    </>
  );
};
